import React from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import type { PendingTileState } from '../types';

interface Props {
  pending: PendingTileState;
  onClick: () => void;
}

function formatEta(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.ceil(seconds / 60)}m`;
  return `${Math.ceil(seconds / 3600)}h`;
}

const PendingAnalysisTile: React.FC<Props> = ({ pending, onClick }) => {
  // Calculate overall upload progress
  const allProgress = [pending.contentProgress, ...pending.resultProgresses];
  const overallPct = Math.round(allProgress.reduce((a, b) => a + b, 0) / allProgress.length);
  
  // Calculate average ETA across all uploading videos
  const allEtas = [
    pending.contentEta,
    ...(pending.resultEtas || []),
  ].filter((eta): eta is number => eta !== undefined && eta > 0);
  const avgEta = allEtas.length > 0 
    ? Math.round(allEtas.reduce((a, b) => a + b, 0) / allEtas.length)
    : 0;

  const stageLabelMap: Record<PendingTileState['stage'], string> = {
    uploading: avgEta > 0 ? `Uploading… ${overallPct}% (${formatEta(avgEta)})` : `Uploading… ${overallPct}%`,
    submitting: 'Submitting…',
    analysing: 'AI analysing…',
    failed: '⚠ Failed',
  };
  const stageLabel = stageLabelMap[pending.stage];

  return (
    <Box
      onClick={onClick}
      sx={{
        borderRadius: '20px',
        overflow: 'hidden',
        cursor: 'pointer',
        border: '2px solid transparent',
        backgroundImage: 'linear-gradient(white, white), linear-gradient(135deg, #F39A81, #959BEA)',
        backgroundOrigin: 'border-box',
        backgroundClip: 'padding-box, border-box',
        boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
        '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 8px 24px rgba(0,0,0,0.14)' },
      }}
    >
      {/* Animated thumbnail area */}
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          paddingTop: '56.25%',
          background: 'linear-gradient(135deg, #F39A81 0%, #959BEA 100%)',
          overflow: 'hidden',
        }}
      >
        {/* Shimmer sweep */}
        <Box
          sx={{
            position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
            background: 'linear-gradient(90deg, transparent 25%, rgba(255,255,255,0.18) 50%, transparent 75%)',
            backgroundSize: '200% 100%',
            '@keyframes parsnipShimmer': {
              '0%': { backgroundPosition: '200% 0' },
              '100%': { backgroundPosition: '-200% 0' },
            },
            animation: pending.stage === 'failed' ? 'none' : 'parsnipShimmer 1.8s linear infinite',
          }}
        />
        {/* Centre indicator */}
        <Box
          sx={{
            position: 'absolute', inset: 0,
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1,
          }}
        >
          {pending.stage === 'failed'
            ? <Typography sx={{ fontSize: '36px' }}>⚠️</Typography>
            : <CircularProgress size={38} thickness={3} sx={{ color: 'rgba(255,255,255,0.9)' }} />
          }
        </Box>
        {/* Pet name badge */}
        {pending.petName && (
          <Box sx={{ position: 'absolute', bottom: 8, left: 8, background: 'rgba(0,0,0,0.55)', borderRadius: '20px', px: 1.5, py: 0.5 }}>
            <Typography sx={{ color: 'white', fontSize: '12px', fontFamily: 'Maven Pro', fontWeight: 600 }}>
              {pending.petName}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Progress bar */}
      <Box sx={{ height: '4px', backgroundColor: '#f0f0f0' }}>
        {pending.stage === 'uploading' && (
          <Box sx={{ height: '100%', width: `${overallPct}%`, background: 'linear-gradient(to right, #F39A81, #959BEA)', transition: 'width 0.35s ease' }} />
        )}
        {pending.stage === 'failed' && (
          <Box sx={{ height: '100%', width: '100%', backgroundColor: '#fed7d7' }} />
        )}
        {(pending.stage === 'submitting' || pending.stage === 'analysing') && (
          <Box
            sx={{
              height: '100%',
              background: 'linear-gradient(to right, #F39A81, #959BEA)',
              backgroundSize: '200% 100%',
              '@keyframes parsnipBar': {
                '0%': { backgroundPosition: '0% 0' },
                '100%': { backgroundPosition: '200% 0' },
              },
              animation: 'parsnipBar 1.4s linear infinite',
            }}
          />
        )}
      </Box>

      {/* Footer */}
      <Box sx={{ padding: '10px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography sx={{ fontFamily: 'Maven Pro', fontSize: '13px', fontWeight: 600, color: pending.stage === 'failed' ? '#e53e3e' : '#959BEA' }}>
          {stageLabel}
        </Typography>
        <Typography sx={{ fontFamily: 'Maven Pro', fontSize: '11px', color: '#bbb' }}>
          {pending.resultFileNames.length} reaction{pending.resultFileNames.length === 1 ? '' : 's'}
        </Typography>
      </Box>
    </Box>
  );
};

export default PendingAnalysisTile;
